/**
 * Staff Model-step panel: released 3D versions + a Loom field on each version.
 * Clients only see the current version’s video; older Looms stay in history.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, History, Save, Video } from 'lucide-react';
import type { PortalFileRelease } from '../../types';
import { parseLoomVideoId } from '../../utils/loomEmbed';
import { setPortalReleaseLoomUrl } from '../../utils/portalFileReleaseService';
import { formatPortalReleaseAddedAt } from '../../utils/portalReleaseDateFormat';
import { loomUrlForRelease } from '../../utils/portalModelLoom';
import { portalHostedAssetHref } from '../../utils/portalAssetUrl';
import './PortalModelVersionLoomPanel.css';

export interface PortalModelVersionLoomPanelProps {
  portalId: string;
  releases: PortalFileRelease[];
  currentReleaseId?: string;
}

const PortalModelVersionLoomPanel: React.FC<PortalModelVersionLoomPanelProps> = ({
  portalId,
  releases,
  currentReleaseId,
}) => {
  const sorted = useMemo(
    () =>
      [...releases]
        .filter(r => r.streamKind === 'three_d_html' && r.id)
        .sort((a, b) => (b.releaseVersionIndex ?? 0) - (a.releaseVersionIndex ?? 0)),
    [releases],
  );

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const serverKey = sorted.map(r => `${r.id}:${loomUrlForRelease(r)}`).join('|');

  useEffect(() => {
    setDrafts(Object.fromEntries(sorted.map(r => [r.id as string, loomUrlForRelease(r)])));
    // serverKey captures id + loomUrl so typing is not reset on unrelated rerenders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey]);

  const saveLoom = async (releaseId: string, raw: string) => {
    const trimmed = raw.trim();
    if (trimmed && !parseLoomVideoId(trimmed)) {
      alert('Please paste a valid Loom link (https://www.loom.com/share/… or …/embed/…).');
      return;
    }
    setSavingId(releaseId);
    try {
      await setPortalReleaseLoomUrl(portalId, releaseId, trimmed);
      setDrafts(prev => ({ ...prev, [releaseId]: trimmed }));
    } catch (err) {
      console.error(err);
      alert('Failed to save the Loom link on this model version.');
    } finally {
      setSavingId(null);
    }
  };

  if (sorted.length === 0) {
    return (
      <div className="pmvl-panel">
        <div className="pmvl-heading">
          <History size={13} aria-hidden />
          <span>Version control &amp; Looms</span>
        </div>
        <p className="pmvl-empty">
          Release a 3D model from Project Hub to start version history. Each version can have its
          own Loom. A new release without a video shows no walkthrough on the client page.
        </p>
      </div>
    );
  }

  return (
    <div className="pmvl-panel">
      <div className="pmvl-heading">
        <History size={13} aria-hidden />
        <span>Version control &amp; Looms</span>
      </div>
      <p className="pmvl-hint">
        Attach a walkthrough to the version it belongs to. Clients see a video only when the
        current model has one. Older Looms stay available in the public version history.
      </p>
      <ul className="pmvl-list">
        {sorted.map(release => {
          const id = release.id as string;
          const draft = drafts[id] ?? loomUrlForRelease(release);
          const saved = loomUrlForRelease(release);
          const dirty = draft.trim() !== saved;
          const isCurrent = id === currentReleaseId;
          const busy = savingId === id;
          return (
            <li key={id} className={`pmvl-row${isCurrent ? ' pmvl-row--current' : ''}`}>
              <div className="pmvl-row-head">
                <span className="pmvl-version">v{release.releaseVersionIndex}</span>
                {isCurrent ? <span className="pmvl-current">Current</span> : null}
                <span className="pmvl-meta">
                  {formatPortalReleaseAddedAt(release.releasedAt) ?? '—'}
                  {release.displayLabel ? ` · ${release.displayLabel}` : ''}
                </span>
                <a
                  className="pmvl-open"
                  href={portalHostedAssetHref(release.releasedUrl, { downloadName: release.name })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open model <ExternalLink size={11} aria-hidden />
                </a>
              </div>
              <label className="pmvl-loom-label">
                <Video size={12} aria-hidden />
                Loom for v{release.releaseVersionIndex}
              </label>
              <div className="pmvl-loom-row">
                <input
                  type="url"
                  className="pm-input"
                  value={draft}
                  onChange={e => setDrafts(prev => ({ ...prev, [id]: e.target.value }))}
                  placeholder="https://www.loom.com/share/… (optional)"
                  disabled={busy}
                />
                {saved ? (
                  <a
                    className="cw-mini-btn"
                    href={saved}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Watch
                  </a>
                ) : null}
                {draft.trim() && (
                  <button
                    type="button"
                    className="cw-mini-btn"
                    disabled={busy}
                    onClick={() => void saveLoom(id, '')}
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  className="cw-mini-btn"
                  disabled={busy || !dirty}
                  onClick={() => void saveLoom(id, draft)}
                >
                  <Save size={11} aria-hidden /> {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PortalModelVersionLoomPanel;
